import React from "react";

import {
  createStyles,
  makeStyles,
  Theme,
  Typography,
  Button,
} from "@material-ui/core";

import config from "../../config";
import i18n from "../../services/localization/i18n";
import { orange } from "@material-ui/core/colors";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    actions: {
      alignContent: "center",
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
    },

    button: {
      flexGrow: 1,
      fontSize: "smaller",
      marginBottom: theme.spacing(),
      marginRight: theme.spacing(2),
    },

    content: {
      alignItems: "center",
      display: "flex",
      flexDirection: "row",
      width: "100%",
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(1),
      [theme.breakpoints.up("xl")]: {
        maxWidth: 1300,
      },
      [theme.breakpoints.only('xs')]: {
        flexDirection: "column"
      },
    },
    cta: {
      alignContent: "center",
      alignItems: "stretch",
      width: "40%",
      padding: theme.spacing(),
      paddingRight: theme.spacing(5),
      textAlign: "center",
      [theme.breakpoints.down('sm')]: {
        paddingRight: theme.spacing(),
      },
      [theme.breakpoints.only('xs')]: {
        width: "100%",
      },
      [theme.breakpoints.only('lg')]: {
        marginLeft: theme.spacing(7),
      }
    },
    ctaButton: {
      flexGrow: 1,
      marginBottom: theme.spacing(),
      marginRight: theme.spacing(2),
      borderRadius: theme.spacing(4),
      padding: theme.spacing(2),
      fontSize: "large",
      minWidth: "60%",
      width: "auto",
      color: "white",
      backgroundColor: orange[800],
      "&:hover": {
        backgroundColor: orange[900],
      },
    },
    ctaGroup: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      marginBottom: theme.spacing(4),
      width: "100%",
    },
    ctaText: {

    },
    head: {
      marginBottom: theme.spacing(2.5),
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(5),
        fontSize: "40px",
      },
      width: 'min-content',
      marginLeft: 'auto',
      marginRight: 'auto'
    },
    image: {
      alignContent: "center",
      width: "100%",
    },
    picture: {
      padding: theme.spacing(),
      maxWidth: 770,
      width: "60%",
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        width: "100%",
      },
      [theme.breakpoints.up('sm')]: {
        width: "50%",
      },
      [theme.breakpoints.up('lg')]: {
        width: "60%"
      },
    },
    root: {
      backgroundColor: "#ffffff",
      backgroundImage:
        "url(https://meet-cdn.azureedge.net/assets/content/sh3.png)",
      backgroundPosition: "53% 9px",
      backgroundRepeat: "no-repeat",
      backgroundSize: "auto",
      height: 750, 
      display: "flex",
      flexDirection: "row",
      flexGrow: 1,
      justifyContent: "center",
      paddingTop: theme.spacing(4),
      [theme.breakpoints.down('md')]: {
        paddingTop: 0,
      },
    },
    subhead: {
      marginBottom: theme.spacing(4),
      width: 'max-content',

      fontSize: '1.3rem',
      [theme.breakpoints.down('sm')]: {
        fontSize: '1.2rem',
      }
    },
    subheadContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center"
      
    },
    terms: {
      fontSize: "smaller",
      color: theme.palette.text.hint,
      margin: theme.spacing(0.5),
    },
    termsLink: {
      color: theme.palette.text.hint,
      margin: theme.spacing(0.5),
      fontSize: "smaller",
    },
  })
);

const General: React.FC = () => {
  
  const classes = useStyles();
  const authenticatedUser = config.me &&  config.me.isAuthenticated && !config.me.isGuest;

  const onClick = () => {
    config.getJoinUrlAsync().then((url) => {
      window.location.href = url;
    });
  };

  return (
    <section className={classes.root}>
      <div className={classes.content}>
        <div className={classes.cta}>
          <Typography variant="h2" className={classes.head}>
            {i18n.t("general_title_text")}
          </Typography>
          <div className={classes.subheadContainer}>
            <Typography variant="h5" className={classes.subhead}>
              {i18n.t("general_cta_text_all")}
            </Typography>
          </div>
          <div className={classes.actions}>
            <div className={classes.ctaGroup}>
              <Button
                className={classes.ctaButton}
                color="primary"
                onClick={onClick}
              >
                { !authenticatedUser && i18n.t("general_cta_button")}
                { authenticatedUser && i18n.t("general_cta_button_quick")}
              </Button>
              <Typography variant="caption" className={classes.ctaText}>
                { !authenticatedUser && i18n.t("cta_nosignup_text")}
              </Typography>
            </div>

            <div className={classes.terms}>
              {i18n.t("general_cta_consent_one")}
              <br />
              {i18n.t("general_cta_consent_two")}
              <br />
              <a
                className={classes.termsLink}
                href="/#/tos"
                title={i18n.t("general_tos_tooltip")}
              >
                {i18n.t("general_tos_text")}
              </a>
              <a
                className={classes.termsLink}
                href="/#/privacy"
                title={i18n.t("general_privacy_tooltip")}
              >
                {i18n.t("general_privacy_text")}
              </a>
              <a
                className={classes.termsLink}
                href="/#/cookies"
                title={i18n.t("general_cookies_text")}
              >
                {i18n.t("general_cookies_text")}
              </a>
              <a
                className={classes.termsLink}
                href="/#/refund"
                title={i18n.t("general_refund_tooltip")}
              >
                {i18n.t("general_refund_text")}
              </a>
            </div>

            <Button
              className={classes.button}
              variant="text"
              color="inherit"
              href={"mailto:office@meet.rs"}
            >
              {i18n.t("general_cta_contact")}
            </Button>
          </div>
        </div>
        <div className={classes.picture}>
          <img
            className={classes.image}
            src="https://meet-cdn.azureedge.net/assets/content/s1.png"
            alt="Whiteboard interview"
          />
        </div>
      </div>
    </section>
  );
};

export default General;
